import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator } from 'react-native';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { MaterialCommunityIcons, MaterialIcons, Ionicons } from '@expo/vector-icons'; // Substituindo por @expo/vector-icons
import { Alert, Platform, PermissionsAndroid } from 'react-native';

const daysOfWeek = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];

const DetailedGainsScreen = ({ navigation }) => {
  const [loading, setLoading] = useState(true);
  const [gainsByDay, setGainsByDay] = useState(Array(7).fill(null).map(() => ({
    ordersCount: 0,
    totalOrderValue: 0,
    totalRouteValue: 0,
    bairroTotal: 0,
    setorTotal: 0,
    extraTotal: 0,
  })));
  const [weekRange, setWeekRange] = useState('');

  const resetGainsData = () => {
    setGainsByDay(Array(7).fill(null).map(() => ({
      ordersCount: 0,
      totalOrderValue: 0,
      totalRouteValue: 0,
      bairroTotal: 0,
      setorTotal: 0,
      extraTotal: 0,
    })));
  };

  useEffect(() => {
    const checkResetTime = () => {
      const now = new Date();
      const isMonday = now.getDay() === 1; // 1 representa segunda-feira
      const isFiveAM = now.getHours() === 5 && now.getMinutes() === 0;
  
      if (isMonday && isFiveAM) {
        const lastReset = localStorage.getItem('lastResetDate');
  
        // Evita múltiplas execuções no mesmo dia
        if (lastReset !== now.toDateString()) {
          resetGainsData();
          localStorage.setItem('lastResetDate', now.toDateString()); // Armazena a última data de reset
        }
      }
    };
  
    // Checa a cada minuto se deve resetar os valores
    const interval = setInterval(checkResetTime, 60000);
  
    return () => clearInterval(interval); // Limpa o intervalo ao desmontar o componente
  }, []);
  

  useEffect(() => {
    navigation.setOptions({ headerShown: false });

    if (!auth.currentUser) {
      Alert.alert('Erro', 'Usuário não autenticado');
      return;
    }

    const calculateWeekRange = () => {
      const now = new Date();
      const dayOfWeek = now.getDay();
      const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - diffToMonday);
      startOfWeek.setHours(0, 0, 0, 0);

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);

      setWeekRange(`${startOfWeek.toLocaleDateString('pt-BR')} - ${endOfWeek.toLocaleDateString('pt-BR')}`);
      return { startOfWeek, endOfWeek };
    };

    const { startOfWeek, endOfWeek } = calculateWeekRange();

    const fetchDetailedGains = async () => {
      setLoading(true);
      try {
        const user = auth.currentUser;
        const qOrders = query(collection(db, 'pedido'), where('companyId', '==', user.uid), where('status', '==', 'completed'));
        const qRoutes = query(collection(db, 'routes'), where('companyId', '==', user.uid), where('status', '==', 'completed'));

        const ordersSnapshot = await getDocs(qOrders);
        const routesSnapshot = await getDocs(qRoutes);
        const orders = ordersSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const routes = routesSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        let gainsData = Array(7).fill(null).map(() => ({
          ordersCount: 0,
          totalOrderValue: 0,
          totalRouteValue: 0,
          bairroTotal: 0,
          setorTotal: 0,
          extraTotal: 0,
        }));

        // Processar pedidos concluídos
        orders.forEach((order) => {
          if (order.completedAt && order.orderValue && order.locationType) {
            const orderDate = new Date(order.completedAt.seconds * 1000);
            if (orderDate >= startOfWeek && orderDate <= endOfWeek) {
              const dayIndex = (orderDate.getDay() + 6) % 7;

              gainsData[dayIndex].ordersCount++;
              gainsData[dayIndex].totalOrderValue += parseFloat(order.orderValue) || 0;

              if (order.locationType === 'Bairro') gainsData[dayIndex].bairroTotal += parseFloat(order.locationValue) || 0;
              if (order.locationType === 'Setor') gainsData[dayIndex].setorTotal += parseFloat(order.locationValue) || 0;
              if (order.locationType === 'Extra') gainsData[dayIndex].extraTotal += parseFloat(order.locationValue) || 0;
            }
          }
        });

        // Processar rotas concluídas
        routes.forEach((route) => {
          if (route.completedAt && route.totalValue) {
            const routeDate = new Date(route.completedAt.seconds * 1000);
            if (routeDate >= startOfWeek && routeDate <= endOfWeek) {
              const dayIndex = (routeDate.getDay() + 6) % 7;
              gainsData[dayIndex].totalRouteValue += parseFloat(route.totalValue) || 0;
            }
          }
        });

        setGainsByDay(gainsData);
      } catch (error) {
        console.error('Erro ao carregar detalhes dos ganhos:', error);
        Alert.alert('Erro', 'Não foi possível carregar os dados.');
      } finally {
        setLoading(false);
      }
    };

    fetchDetailedGains();
  }, []);

  const generatePDF = async () => {
    try {
      if (Platform.OS !== 'web') {
        Alert.alert("Erro", "A geração de PDF só está disponível na versão Web.");
        return;
      }
  
      // 📌 Importação dinâmica das bibliotecas para evitar erros no React Native
      const { jsPDF } = await import('jspdf');
      const html2canvas = (await import('html2canvas')).default;
  
      // 📌 Seleciona o elemento do relatório (certifique-se de que o ID está correto)
      const element = document.getElementById('report-content');
      if (!element) {
        console.error("Elemento do relatório não encontrado.");
        return;
      }
  
      // 📌 Configuração para capturar a tela inteira e evitar cortes na parte inferior
      const canvas = await html2canvas(element, {
        scale: window.innerWidth < 768 ? 2 : 3, // Aumenta a escala para melhor qualidade
        useCORS: true, // Permite capturar imagens externas corretamente
        scrollX: 0, // Evita capturar a posição de rolagem
        scrollY: -window.scrollY, // Corrige o posicionamento da rolagem
        width: element.scrollWidth, // Usa a largura real do conteúdo
        height: element.scrollHeight + 100, // 📌 Garante espaço extra na parte inferior
      });
  
      const imgData = canvas.toDataURL('image/png');
  
      // 📌 Ajusta o tamanho do PDF para capturar todo o conteúdo corretamente
      const pdfWidth = window.innerWidth < 768 ? 180 : 210; // 📌 Largura ajustada para celular
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width; // Mantém a proporção exata
  
      // 📌 Criar PDF no formato correto
      const pdf = new jsPDF({
        orientation: 'portrait', // 'portrait' = retrato | 'landscape' = paisagem
        unit: 'mm',
        format: [pdfWidth, pdfHeight], // 📌 Ajusta dinamicamente o tamanho do PDF
      });
  
      pdf.addImage(imgData, 'PNG', 10, 10, pdfWidth - 20, pdfHeight - 20);
      pdf.save('Relatorio_Ganhos.pdf');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      Alert.alert('Erro', 'Não foi possível gerar o PDF.');
    }
  };

  const formatCurrency = (value) => {
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="large" color="#FFF" />
      </View>
    );
  }

  return (
    <ScrollView 
  contentContainerStyle={styles.scrollContainer} 
  style={styles.fullScreen}
  showsVerticalScrollIndicator={false}
>
  <View id="report-content" style={styles.container}>

    <TouchableOpacity style={styles.backButtonContainer} onPress={() => navigation.goBack()}>
      <MaterialIcons name="arrow-back" size={24} color="#FFF" />
    </TouchableOpacity>

    <Text style={styles.dateRange}>{weekRange}</Text>
    <Text style={styles.title}>Ganhos Detalhados</Text>

    {gainsByDay.map((gains, index) => (
      <View key={index} style={styles.gainItem}>
        <Text style={styles.dayTitle}>{daysOfWeek[index]}</Text>
        <Text style={styles.gainText}>Pedidos concluídos: {gains.ordersCount}</Text>
        <Text style={styles.gainText}>Valor total dos pedidos: {formatCurrency(gains.totalOrderValue)}</Text>
        <Text style={styles.gainText}>Valor total das rotas: {formatCurrency(gains.totalRouteValue)}</Text>
        <Text style={styles.gainText}>Total Bairro: {formatCurrency(gains.bairroTotal)}</Text>
        <Text style={styles.gainText}>Total Setor: {formatCurrency(gains.setorTotal)}</Text>
        <Text style={styles.gainText}>Total Extra: {formatCurrency(gains.extraTotal)}</Text>
      </View>
    ))}

    <TouchableOpacity style={styles.pdfButton} onPress={generatePDF}>
      <Text style={styles.pdfButtonText}>Gerar Relatório (PDF)</Text>
    </TouchableOpacity>
  </View>
</ScrollView>

  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    flexGrow: 1,
    alignItems: 'center',
    paddingBottom: 20,
  },
  container: {
    minHeight: '100%', // 📌 Faz o fundo cobrir toda a tela
    backgroundColor: '#2c5364',
    paddingTop: 40,
    paddingBottom: 40,
    paddingHorizontal: 16,
    alignItems: 'center',
    width: '100%',
  },
  fullScreen: {
    flex: 1,
    backgroundColor: '#2c5364', // Cor de fundo extendida
  },  
  dateRange: {
    fontSize: 18,
    color: '#00FF9D',
    textAlign: 'center',
    marginBottom: 10,
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#00FF9D',
    marginBottom: 20,
  },
  dayTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFF',
    marginBottom: 10,
  },
  gainItem: {
    backgroundColor: '#203a43',
    padding: 15,
    marginVertical: 8,
    borderRadius: 8,
    width: '95%',
    alignSelf: 'center',
  },
  gainText: {
    fontSize: 16,
    color: '#FFF',
  },
  backButtonContainer: {
    position: 'absolute',
    top: 40, // Posição no topo da tela
    left: 20, // Alinhado ao canto esquerdo com espaçamento
    backgroundColor: '#203a43',
    padding: 10,
    borderRadius: 50,
    zIndex: 10, // Garante que fique acima de outros elementos
  },
  pdfButton: {
    backgroundColor: '#00FF9D',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 20,
    width: '90%',
  },
  pdfButtonText: {
    color: '#FFF',
    fontSize: 18,
    fontWeight: 'bold',
  },
  reportContainer: {
    width: '95%', // 📌 Ajustado para ocupar melhor a tela no celular
    maxWidth: 900, // 📌 Aumentado para telas grandes
    padding: 25,
    backgroundColor: 'white', // Fundo branco para melhor visibilidade no PDF
    borderRadius: 10, // Bordas arredondadas para melhorar o design
    overflow: 'hidden', // Evita elementos saindo do layout
    margin: 'auto', // Centraliza na tela
  },  
});

export default DetailedGainsScreen;
